import { createContext } from 'react-router';

export const SFDC_MAINTENANCE_HEADER = 'sfdc_maintenance';

/** Only allow root-relative maintenance destinations, preserving queries and fragments. */
export function getMaintenanceReturnTo(value: string | null): string {
    // Browsers interpret backslashes as slashes and strip tabs/newlines from URLs.
    // eslint-disable-next-line no-control-regex
    if (!value?.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u001f\u007f]/u.test(value)) {
        return '/';
    }

    const base = 'https://maintenance.invalid';
    const url = new URL(value, base);
    // Dot-segment normalization can turn /a/..//host into a protocol-relative path.
    if (url.origin !== base || url.pathname.startsWith('//')) {
        return '/';
    }
    return `${url.pathname}${url.search}${url.hash}`;
}

export type Maintenance = {
    set: <T>(req: Request, promise: Promise<T>) => Promise<T>;
    gate: (req: Request) => boolean;
    markDetected: () => void;
    readonly seen: boolean;
    readonly size: number;
    readonly promise: Promise<boolean>;
};

export function hasSfdcMaintenanceHeader(headers: { get(name: string): string | null } | undefined | null): boolean {
    if (!headers) {
        return false;
    }
    const value = headers.get(SFDC_MAINTENANCE_HEADER);
    return value === 'site' || value === 'system';
}

/**
 * True when a SCAPI response signals site/system maintenance. A bare 503 is not enough: custom APIs
 * use it for upstream failures and SCAPI for load shedding. An HTML 503 is the platform maintenance
 * page, which API handlers (always JSON) never produce.
 */
export function isMaintenanceResponse(response: Response): boolean {
    return (
        hasSfdcMaintenanceHeader(response.headers) ||
        (response.status === 503 && Boolean(response.headers.get('content-type')?.includes('text/html')))
    );
}

function headersFromUnknown(error: unknown): { get(name: string): string | null } | undefined {
    if (error instanceof Response) {
        return error.headers;
    }
    if (typeof error === 'object' && error !== null && 'headers' in error) {
        const headers = (error as { headers: unknown }).headers;
        if (headers && typeof headers === 'object' && 'get' in headers && typeof headers.get === 'function') {
            return headers as { get(name: string): string | null };
        }
    }
    if (error instanceof Error && error.cause !== undefined) {
        return headersFromUnknown(error.cause);
    }
    return undefined;
}

/**
 * Creates a new maintenance context object.
 */
export function createMaintenance(): Maintenance {
    const requests = new Map<Request, Promise<unknown>>();

    let isLocked = false;
    let seenHeader = false;
    // Outcome of the first SCAPI response after gate(). Must survive requests.clear()
    // so middleware can still see a header-based 503 if it reads .promise later.
    let firstDecision: Promise<boolean> | undefined;

    const settleFrom = (promise: Promise<unknown>): Promise<boolean> =>
        promise.then(
            (): boolean => {
                isLocked = true;
                requests.clear();
                return true;
            },
            (reason?: unknown) => {
                isLocked = true;
                requests.clear();
                throw reason;
            }
        );

    const obj = {
        set: <T = unknown>(req: Request, promise: Promise<T>): Promise<T> => {
            !isLocked && requests.set(req, promise);
            return promise;
        },
        gate: (req: Request): boolean => {
            if (!requests.has(req)) {
                return false;
            }
            if (!isLocked) {
                isLocked = true;
                const firstPromise = requests.get(req);
                requests.clear();
                if (firstPromise) {
                    firstDecision = settleFrom(firstPromise);
                }
                return true;
            }
            return false;
        },
        markDetected: (): void => {
            seenHeader = true;
        },
    };

    Object.defineProperty(obj, 'size', { get: () => requests.size });
    Object.defineProperty(obj, 'seen', { get: () => seenHeader });
    Object.defineProperty(obj, 'promise', {
        get: () => {
            if (firstDecision) {
                return firstDecision;
            }
            if (isLocked || requests.size === 0) {
                return Promise.resolve(false);
            }
            return settleFrom(Promise.race(requests.values()));
        },
    });

    return obj as Maintenance;
}

/**
 * Context key for the maintenance data.
 */
export const maintenanceContext = createContext<Maintenance>();

/**
 * True when a loader, SCAPI client, or route error represents site/system maintenance.
 * Detects the SFCC `sfdc_maintenance` header, legacy MAINTENANCE_ERROR strings, and SCAPI bodies
 * that include the site/system-under-maintenance error type. A bare 503 is not maintenance.
 */
export function isMaintenanceError(error: unknown): boolean {
    if (hasSfdcMaintenanceHeader(headersFromUnknown(error))) {
        return true;
    }

    const message = typeof error === 'string' ? error : error instanceof Error ? error.message : undefined;

    return (
        typeof message === 'string' &&
        (message.includes('MAINTENANCE_ERROR') ||
            message.includes('site-under-maintenance') ||
            message.includes('system-under-maintenance'))
    );
}

/**
 * Prefer a maintenance failure when several parallel SCAPI calls reject.
 * `Promise.all` would leave the other rejections unhandled and crash the MRT worker.
 */
export function preferredSettledRejection(rejections: PromiseRejectedResult[]): unknown {
    if (rejections.length === 0) {
        return undefined;
    }
    return (
        rejections.find((result) => isMaintenanceError(result.reason)) ??
        rejections.find((result) => result.reason instanceof Response) ??
        rejections[0]
    ).reason;
}
